import { toast as sonnerToast } from "sonner";
import { translations } from "@/contexts/LanguageContext";
import { mapSupabaseError } from "./supabase-errors";
import { insufficientStockMessage } from "./insufficientStockError";
import { logger } from "./logger";
import { isSessionAbsentError, isSessionMissingError } from "./auth/sessionGuardedFetch";
import { isClockSkewSuppressing } from "./auth/clockSkew";
import { goToSignIn } from "./auth/reconnect";

type Language = "fr" | "en";

export const toast = sonnerToast;

function getCurrentLanguage(): Language {
  if (typeof window !== "undefined") {
    try {
      // Safari lève SecurityError quand le stockage du site est interdit (Sentry JAVASCRIPT-REACT-58)
      const stored = window.localStorage.getItem("app_language");
      if (stored === "en" || stored === "fr") return stored;
    } catch {
      // Stockage inaccessible : ignorer et passer à la langue du navigateur
    }
  }
  if (typeof navigator !== "undefined") {
    const browserLang = navigator.language?.toLowerCase() ?? "";
    if (browserLang.startsWith("en")) return "en";
  }
  return "fr";
}

function translate(key: string, language: Language): string {
  return translations[language][key as keyof (typeof translations)[Language]] ?? key;
}

/**
 * Traduit une clé dans la langue courante, hors de tout composant React.
 * Réservé au code non-React (gestionnaires d'erreur globaux) : dans un
 * composant, utiliser le `t` du LanguageContext.
 */
export function translateNow(key: string): string {
  return translate(key, getCurrentLanguage());
}

/**
 * Show an error toast.
 *
 *   errorToast(t("Inventory.toast.createFailed"))
 *   errorToast(error, t("Inventory.toast.createFailed"))
 *
 * When passed an error object, the Supabase mapper is consulted first; if the
 * error is recognized the mapped translated message is shown, otherwise the
 * contextual fallback string is shown. The original error is always logged.
 */
export function errorToast(message: string): void;
export function errorToast(error: unknown, fallback: string): void;
export function errorToast(arg1: unknown, fallback?: string): void {
  // Horloge du poste en avance détectée par AuthContext
  // (`src/lib/auth/clockSkew.ts`). La déconnexion qu'il programme fait partir
  // des requêtes en anonyme, et les écrans affichent alors de FAUX messages :
  // « Accès refusé » (OwnerAuth), « Aucun salon lié » (OwnerDashboard). Pendant
  // la fenêtre, le seul message vrai les remplace tous.
  //
  // ⚠️ Placé AVANT la branche à une seule chaîne, pour couvrir les deux formes
  // d'appel. Le journal est conservé : se taire à l'écran ne doit pas rendre le
  // cas indiagnosticable.
  if (isClockSkewSuppressing(performance.now())) {
    logger.error("[errorToast] remplacé par le message d'horloge", arg1);
    clockSkewToast();
    return;
  }

  if (typeof arg1 === "string" && fallback === undefined) {
    sonnerToast.error(arg1);
    return;
  }

  logger.error("[errorToast]", arg1);

  // La porte (`src/lib/auth/sessionGuardedFetch.ts`) a retenu cet appel. Le
  // message affiché est le toast PARTAGÉ `sessionExpiredToast` (clé i18n
  // `errors.session.expired`), le même que celui de `recoverFromAuthError`
  // (`src/lib/authErrorRecovery.ts`) : son id stable fait que sonner met à
  // jour un seul toast au lieu d'en empiler un par appel, qu'il vienne d'ici
  // ou de là bas, même en rafale de 36 fichiers.
  //
  // ⚠️ Le journal reste au dessus de ce test, volontairement : se taire à
  // l'écran ne doit pas rendre le cas indiagnosticable.
  if (isSessionMissingError(arg1)) {
    sessionExpiredToast();
    return;
  }

  // Retenue SILENCIEUSE de la porte : une page privée qui se démonte sans
  // session (déconnexion volontaire, redirection de `ProtectedRoute`). Aucun
  // message n'y est vrai, ni « session expirée » ni le générique. Le journal,
  // au dessus, garde la trace.
  if (isSessionAbsentError(arg1)) return;

  const language = getCurrentLanguage();

  // Refus de stock du déclencheur (`INSUFFICIENT_STOCK {json}`) : la phrase
  // nomme le produit, l'emplacement et le reste. Passe AVANT le mappeur, qui
  // ne rendrait que « Stock insuffisant » sans le détail.
  const stockMessage = insufficientStockMessage(arg1, (key) => translate(key, language));
  if (stockMessage) {
    sonnerToast.error(stockMessage);
    return;
  }

  const mappedKey = mapSupabaseError(arg1);
  const message = mappedKey
    ? translate(mappedKey, language)
    : (fallback ?? translate("errors.generic", language));
  sonnerToast.error(message);
}

/**
 * Le message de session expirée, et lui seul.
 *
 * ⚠️ L'`id` stable est le coeur du mécanisme : sonner MET À JOUR un toast qui
 * porte un id déjà affiché au lieu d'en empiler un second. Une rafale de
 * montage d'écran peut appeler ceci depuis 36 fichiers plus une fois depuis le
 * rattrapage, et l'utilisateur ne voit qu'un seul message.
 *
 * Il existe parce que `recoverFromAuthError` VERROUILLE `sessionDeclaredDead`
 * après le premier échec de renouvellement et cesse alors de parler. Sans ce
 * chemin ci, la deuxième rafale ne produisait plus AUCUN message : la porte
 * rendait son 401 marqué et `errorToast` se taisait. C'était une régression
 * vers le silence que ce chantier existe pour supprimer.
 *
 * Le toast porte une action « Se reconnecter » (`goToSignIn`,
 * `src/lib/auth/reconnect.ts`) : elle efface la session locale puis rouvre la
 * bonne page de connexion, avec un retour automatique vers la page quittée.
 */
export const SESSION_EXPIRED_TOAST_ID = "flowcut-session-expired";

export function sessionExpiredToast(): void {
  // Pendant une horloge décalée, « session expirée » serait un faux message de
  // plus : la session n'a pas expiré, c'est le poste qui avance.
  if (isClockSkewSuppressing(performance.now())) {
    clockSkewToast();
    return;
  }
  sonnerToast.error(translateNow("errors.session.expired"), {
    id: SESSION_EXPIRED_TOAST_ID,
    action: {
      label: translateNow("errors.session.reconnect"),
      onClick: () => {
        void goToSignIn();
      },
    },
  });
}

/**
 * Le message d'horloge de poste en avance, et lui seul.
 *
 * ⚠️ L'`id` stable fait que sonner MET À JOUR le toast déjà affiché au lieu
 * d'en empiler un par appel : la détection se répète à chaque rafraîchissement,
 * et les faux messages remplacés par le filtre d'`errorToast` arrivent en
 * rafale. La durée longue laisse le temps de lire la consigne, qui demande
 * d'agir dans les réglages de Windows.
 */
export const CLOCK_SKEW_TOAST_ID = "flowcut-clock-skew";
export const CLOCK_SKEW_TOAST_DURATION_MS = 15_000;

export function clockSkewToast(): void {
  sonnerToast.error(translateNow("Auth.toast.clockSkew"), {
    id: CLOCK_SKEW_TOAST_ID,
    duration: CLOCK_SKEW_TOAST_DURATION_MS,
  });
}

/**
 * Affiche un succès, sauf pendant la fenêtre d'horloge décalée.
 *
 * Un succès annoncé juste avant une déconnexion pour horloge décalée serait un
 * mensonge (« connexion réussie » alors que la personne va être renvoyée à
 * l'écran de connexion) : on se tait, le message d'horloge est déjà affiché.
 * Contrairement à `errorToast`, on ne remplace pas par `clockSkewToast()` ici :
 * un succès n'est pas un échec à remplacer, seulement un message à taire.
 */
export function successToast(message: string): void {
  if (isClockSkewSuppressing(performance.now())) return;
  sonnerToast.success(message);
}

/**
 * Information neutre : rien n'a échoué, mais le geste n'a pas tout fait
 * (ex. un préréglage rogné au plafond d'un gérant). Muet pendant une dérive
 * d'horloge, comme `successToast`.
 */
export function infoToast(message: string): void {
  if (isClockSkewSuppressing(performance.now())) return;
  sonnerToast.info(message);
}

/**
 * Succès avec une action « Annuler » (~6 s). Muet pendant une dérive d'horloge,
 * comme `successToast`.
 */
export function undoableSuccessToast(message: string, undoLabel: string, onUndo: () => void): void {
  if (isClockSkewSuppressing(performance.now())) return;
  sonnerToast.success(message, { duration: 6000, action: { label: undoLabel, onClick: onUndo } });
}

/**
 * Avertissement visible : l'opération demandée a réussi, mais quelque chose
 * réclame une action de l'utilisateur. À réserver aux cas où se taire laisserait
 * une incohérence réelle (ex. pourboire sorti du tiroir sans mouvement de caisse).
 */
export function warningToast(message: string, options?: { duration?: number }): void {
  sonnerToast.warning(message, options);
}
